'use client'

import { isSafeApp, subscribeToSafeDetection } from '@/config/safe/isSafeApp'
import { useSafeAutoConnect } from '@/config/safe/useSafeAutoConnect'
import { getConfig, getSafeConfig } from '@/config/wagmi/index'
import { SubaccountProvider } from '@/domain/euler/subaccounts'
import { PrivyProvider } from '@privy-io/react-auth'
import { WagmiProvider as PrivyWagmiProvider } from '@privy-io/wagmi'
import { QueryClientProvider } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { useSyncExternalStore } from 'react'
import { Toaster } from 'react-hot-toast'
import { WagmiProvider } from 'wagmi'
import { useWalletIdentify } from '@/features/analytics'
import { WalletSessionSync } from './features/auth/WalletSessionSync'
import { SUPPORTED_CHAINS, SUPPORTED_CHAINS_DEFAULT, privyConfig } from './config/chain/constants'
import { queryClient } from './config/query-client'
import { installPerfApi, instrumentQueryCache, isPerfEnabled } from './domain/perf'
import { TooltipProvider } from './ui/atoms/tooltip/Tooltip'
import { ConfettiProvider } from './ui/molecules/confetti/Confetti'

if (typeof window !== 'undefined' && isPerfEnabled()) {
  // Idempotent across fast-refresh: stash a marker on window to skip re-init.
  // The unsubscribe handle is retained on the same marker so a future teardown
  // path (HMR, explicit reinit) can call `__perfUnsubscribe()` to detach the
  // QueryCache subscription.
  const w = window as unknown as { __perfInstalled?: boolean; __perfUnsubscribe?: () => void }
  if (!w.__perfInstalled) {
    w.__perfInstalled = true
    w.__perfUnsubscribe = instrumentQueryCache(queryClient)
    installPerfApi()
  }
}

const privyConfig_ = getConfig()

let _safeConfig: ReturnType<typeof getSafeConfig> | undefined
function getSafeConfigLazy() {
  if (!_safeConfig) _safeConfig = getSafeConfig()
  return _safeConfig
}

const toasterConfig = {
  position: 'bottom-right' as const,
  containerClassName: 'toast-notifications',
  toastOptions: {
    style: {
      backgroundColor: '#0E0E10',
      border: '1px solid hsla(0, 0%, 100%, 0.04)',
      borderRadius: '8px',
      color: 'white',
      fontSize: '14px',
    },
  },
}

function SafeAutoConnector({ children }: React.PropsWithChildren) {
  useSafeAutoConnect()
  return <>{children}</>
}

function AppShell({ children }: React.PropsWithChildren) {
  useWalletIdentify()

  return (
    <SubaccountProvider>
      <ConfettiProvider>
        <WalletSessionSync />
        <Toaster {...toasterConfig} />
        <TooltipProvider delayDuration={0}>{children}</TooltipProvider>
      </ConfettiProvider>
    </SubaccountProvider>
  )
}

function WalletProviders({ children }: React.PropsWithChildren) {
  const safeDetected = useSyncExternalStore(
    subscribeToSafeDetection,
    () => isSafeApp(),
    () => false,
  )

  if (safeDetected) {
    return (
      <WagmiProvider config={getSafeConfigLazy()} reconnectOnMount>
        <SafeAutoConnector>{children}</SafeAutoConnector>
      </WagmiProvider>
    )
  }
  return <PrivyWagmiProvider config={privyConfig_}>{children}</PrivyWagmiProvider>
}

function App({ children }: React.PropsWithChildren) {
  const pathname = usePathname()
  return (
    <PrivyProvider
      appId={process.env.NEXT_PUBLIC_PRIVY_APP_ID as string}
      config={{
        ...privyConfig,
        supportedChains: (() => {
          if (pathname === '/bridge') {
            return SUPPORTED_CHAINS
          }
          return SUPPORTED_CHAINS_DEFAULT
        })(),
      }}
    >
      <QueryClientProvider client={queryClient}>
        <WalletProviders>
          <AppShell>{children}</AppShell>
        </WalletProviders>
      </QueryClientProvider>
    </PrivyProvider>
  )
}

export default App
